import { useState, useEffect, useCallback } from "react";
import { FileText, Loader2, Search, ChevronDown, ChevronRight, Download, FileOutput, FileInput, TrendingUp, Banknote, Building2, Car } from "lucide-react";
import { apiFetch, openPdf } from "@/portal/api";
import { fmtCZK, invoiceStatus } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";

const TYPE_LABEL = { rent: "Nájemné", deposit: "Kauce", fine: "Pokuta", service: "Servis", insurance: "Pojištění", other: "Ostatní" };
const fmtD = (s) => (s ? new Date((s.length <= 10 ? s + "T00:00:00" : s)).toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric" }) : "—");

const SummaryCard = ({ label, value, sub, icon: Icon, tone = "" }) => (
  <div className="glass rounded-2xl p-5" data-testid={`faktury-card-${label}`}>
    <div className={`flex items-center gap-2 text-xs font-medium mb-1 ${tone || "text-zinc-400"}`}><Icon className="w-4 h-4" /> {label}</div>
    <p className={`text-2xl font-bold font-['Cabinet_Grotesk'] ${tone || ""}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
  </div>
);

const FakturyTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState("issued"); // issued | received
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => new Set());

  const load = useCallback(async () => {
    try { setData(await apiFetch("/admin/finance/faktury", { token })); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;
  if (!data) return <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="faktury-error">Faktury se nepodařilo načíst.</div>;

  const { issued = [], received = [], summary = {} } = data;
  const ql = q.trim().toLowerCase();
  const issuedF = !ql ? issued : issued.filter((i) => [i.number, i.client_name, i.car_label, TYPE_LABEL[i.invoice_type]].filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));
  const receivedF = !ql ? received : received.filter((r) => [r.supplier, r.invoice_number, r.category_label, r.car_label, r.note].filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));

  return (
    <div className="space-y-6" data-testid="faktury-tab">
      <div className="flex items-center gap-2">
        <FileText className="w-6 h-6 text-orange-400" />
        <h2 className="font-['Cabinet_Grotesk'] text-2xl font-bold">Faktury</h2>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Vydané celkem" value={fmtCZK(summary.issued_total)} sub={`${summary.issued_count || 0} faktur`} icon={FileOutput} />
        <SummaryCard label="Zaplaceno" value={fmtCZK(summary.issued_paid)} icon={Banknote} tone="text-green-300" />
        <SummaryCard label="Nezaplaceno" value={fmtCZK(summary.issued_unpaid)} icon={TrendingUp} tone="text-amber-300" />
        <SummaryCard label="Přijaté celkem" value={fmtCZK(summary.received_total)} sub={`${summary.received_count || 0} dokladů`} icon={FileInput} tone="text-red-300" />
      </div>

      {/* Sub-tabs + search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1" data-testid="faktury-subtabs">
          <button onClick={() => setSub("issued")} className={`px-4 py-2 text-sm font-medium rounded-full inline-flex items-center gap-2 transition-colors ${sub === "issued" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid="faktury-tab-issued">
            <FileOutput className="w-4 h-4" /> Vydané ({issued.length})
          </button>
          <button onClick={() => setSub("received")} className={`px-4 py-2 text-sm font-medium rounded-full inline-flex items-center gap-2 transition-colors ${sub === "received" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid="faktury-tab-received">
            <FileInput className="w-4 h-4" /> Přijaté ({received.length})
          </button>
        </div>
        <div className="relative sm:w-64">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat…" className="input-dark pl-9" data-testid="faktury-search" />
        </div>
      </div>

      {/* VYDANÉ */}
      {sub === "issued" && (
        issuedF.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="faktury-issued-empty">Žádné vydané faktury.</div>
        ) : (
          <div className="space-y-2" data-testid="faktury-issued-list">
            {issuedF.map((inv) => {
              const st = invoiceStatus(inv.status);
              const isOpen = open.has(inv.id);
              return (
                <div key={inv.id} className="glass rounded-xl overflow-hidden" data-testid={`faktura-issued-${inv.id}`}>
                  <button onClick={() => toggle(inv.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-white/[0.03] transition-colors" data-testid={`faktura-issued-toggle-${inv.id}`}>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{inv.number || "—"} · <span className="text-zinc-400">{inv.client_name}</span></p>
                      <p className="text-[11px] text-zinc-500">{TYPE_LABEL[inv.invoice_type] || "Nájemné"}{inv.car_label ? ` · ${inv.car_label}` : ""} · vystaveno {fmtD(inv.issued_on)}</p>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${st.cls}`}>{st.label}</span>
                    <span className="text-sm font-semibold shrink-0 w-28 text-right">{fmtCZK(inv.amount)}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid={`faktura-issued-detail-${inv.id}`}>
                      <Detail label="Klient" value={inv.client_name} />
                      <Detail label="Typ" value={TYPE_LABEL[inv.invoice_type] || "Nájemné"} />
                      <Detail label="Vozidlo" value={inv.car_label || "—"} />
                      <Detail label="Popis" value={inv.description || "—"} />
                      <Detail label="Vystaveno" value={fmtD(inv.issued_on)} />
                      <Detail label="Splatnost" value={fmtD(inv.due_on)} />
                      <Detail label="Zaplaceno" value={inv.paid_on ? fmtD(inv.paid_on) : "—"} />
                      <Detail label="Částka" value={fmtCZK(inv.amount)} />
                      <div className="sm:col-span-2 pt-1">
                        <button onClick={() => openPdf(`/admin/invoices/${inv.id}/pdf`, token).catch((e) => toast.error(e.message))} className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 transition-colors" data-testid={`faktura-issued-pdf-${inv.id}`}>
                          <Download className="w-3.5 h-3.5" /> Otevřít PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* PŘIJATÉ */}
      {sub === "received" && (
        <>
          <p className="text-[11px] text-zinc-500 -mt-2">Přijaté faktury se skládají z evidovaných <strong className="text-zinc-300">Nákladů</strong>. Dodavatele a číslo faktury doplníte v sekci Náklady.</p>
          {receivedF.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="faktury-received-empty">Žádné přijaté faktury (náklady).</div>
          ) : (
            <div className="space-y-2" data-testid="faktury-received-list">
              {receivedF.map((r) => {
                const isOpen = open.has(r.id);
                return (
                  <div key={r.id} className="glass rounded-xl overflow-hidden" data-testid={`faktura-received-${r.id}`}>
                    <button onClick={() => toggle(r.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-white/[0.03] transition-colors" data-testid={`faktura-received-toggle-${r.id}`}>
                      {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{r.supplier || r.category_label}{r.invoice_number ? ` · č. ${r.invoice_number}` : ""}</p>
                        <p className="text-[11px] text-zinc-500">{r.category_label}{r.car_label ? ` · ${r.car_label}` : ""} · {fmtD(r.date)}</p>
                      </div>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 shrink-0">přijatá</span>
                      <span className="text-sm font-semibold text-red-300 shrink-0 w-28 text-right">{fmtCZK(r.amount)}</span>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 border-t border-white/5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid={`faktura-received-detail-${r.id}`}>
                        <Detail label="Dodavatel" value={r.supplier || "—"} icon={Building2} />
                        <Detail label="Číslo faktury" value={r.invoice_number || "—"} />
                        <Detail label="Kategorie" value={r.category_label} />
                        <Detail label="Vozidlo" value={r.car_label || "—"} icon={Car} />
                        <Detail label="Datum" value={fmtD(r.date)} />
                        <Detail label="Částka" value={fmtCZK(r.amount)} />
                        {r.note && <div className="sm:col-span-2"><Detail label="Poznámka" value={r.note} /></div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Detail = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-1.5">
    <span className="text-zinc-500 text-xs w-28 shrink-0 pt-0.5">{label}</span>
    <span className="font-medium inline-flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-zinc-500" />}{value}</span>
  </div>
);

export default FakturyTab;
